import React, { useMemo, useState, useEffect, useContext } from 'react';
import styled, { ThemeConsumer, ThemeProvider } from 'styled-components';
import computeScrollIntoView from 'compute-scroll-into-view';
import shortid from 'shortid';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import Loader from '../../Loader/Loader';
import { DropdownWrapper } from '../../Dropdown/Dropdown';
import DropdownItem from '../../Dropdown/components/DropdownItem/DropdownItem';
import DropdownButton from '../../Dropdown/components/DropdownButton/DropdownButton';
import DropdownBox from '../../Dropdown/components/DropdownBox/DropdownBox';
import DropdownHeader from '../../Dropdown/components/DropdownHeader/DropdownHeader';
import DropdownSeparator from '../../Dropdown/components/DropdownSeparator/DropdownSeparator';
import useClickOutside from '../../../hooks/useClickOutside';
import useCloseOnScroll from '../../../hooks/useCloseOnScroll';

import { ErrorText } from '../Controller/components/ControllerError/ControllerError';

import NewTableContext from '../../NewTable/NewTableContext';
import useDoppelganger, { renderFloatingBox } from '../hooks/useDoppelganger';

import { NewTypeaheadProps } from '../../NewTypeahead/NewTypeahead';

function scrollIntoView(node, menuNode) {
  if (!node || !menuNode) {
    return;
  }

  const actions = computeScrollIntoView(node, {
    boundary: menuNode,
    block: 'nearest',
    scrollMode: 'if-needed',
  });

  actions.forEach(({ el, top, left }) => {
    /* eslint-disable no-param-reassign */
    el.scrollTop = top;
    el.scrollLeft = left;
  });
}

const ErrorMessage = styled(ErrorText)`
  margin-top: 4px;
`;

const SKIP_KEYBOARD_NAV = [DropdownHeader.displayName, DropdownSeparator.displayName];
const LoadingState = () => <DropdownItem role="option" disabled render={() => <Loader isLoading />} />;

function CoreTypeahead(props: NewTypeaheadProps) {
  const [invalid, setInvalid] = useState(false);
  const [focusedItem, setFocusedItem] = useState(-1);
  const { scrollableWrapperRef } = useContext(NewTableContext);
  const [showOptions, setShowOptions, ref, self] = useClickOutside();
  const [upItem, setUpItem] = useState('');
  const [downItem, setDownItem] = useState('');

  const alternatePortalContainer = props.portalContainer?.current;

  const doppelganger = useDoppelganger(ref, scrollableWrapperRef, showOptions, alternatePortalContainer);
  useCloseOnScroll(showOptions, setShowOptions, scrollableWrapperRef?.current);

  const {
    value,
    onChange,
    options,
    loading,
    optionLabel,
    filterOptions,
    input,
    option,
    emptyMessage,
    keepOpen,
    allowCustom,
    scrollable,
    width,
    dropdownWidth,
    dropdownMaxHeight,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    ...otherProps
  } = props;

  const name = useMemo(() => `${props.id}-${shortid.generate()}`, [props.id]);
  const filteredOptions = filterOptions ? filterOptions(value) : options;
  const optionsToDisplay = value ? filteredOptions : options;

  useEffect(() => {
    if (!showOptions) {
      setFocusedItem(-1);

      if (allowCustom || !value) {
        setInvalid(false);
        return;
      }

      setInvalid(!options.map(optionLabel).includes(value));
    }
  }, [showOptions, allowCustom, options, optionLabel, value]);

  useEffect(() => {
    const size = optionsToDisplay.length;

    if (!option) {
      return;
    }

    let indexUp = 0;
    let indexDown = 0;

    if (focusedItem > 0) {
      indexUp = focusedItem - 1;
    } else {
      indexUp = size - 1;
    }

    if (focusedItem < size - 1) {
      indexDown = focusedItem + 1;
    } else {
      indexDown = 0;
    }

    const upOption = filteredOptions[indexUp];
    if (upOption) {
      const renderedUpOption = option(upOption);
      renderedUpOption && setUpItem(renderedUpOption.type.displayName);
    }

    const downOption = filteredOptions[indexDown];
    if (downOption) {
      const renderedDownOption = option(downOption);
      renderedDownOption && setDownItem(renderedDownOption.type.displayName);
    }
  }, [option, focusedItem, filteredOptions, optionsToDisplay]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFocusedItem(-1);
    setShowOptions(true);
    onChange(e, { value, option });
  };

  const handleFocus = () => {
    setShowOptions(true);
    setInvalid(false);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, option: any) => {
    if (!keepOpen) {
      setShowOptions(false);
    }
    setFocusedItem(-1);
    onChange(e, { value: optionLabel(option), option });
  };

  // Uses the index of the item to both set the focusedItem state and scroll based on the pseudo focus.
  const focusItem = itemIndex => {
    if (isNil(itemIndex)) return;
    setFocusedItem(itemIndex);
    if (scrollable) scrollIntoView(ref?.current?.getElementsByTagName('li')[itemIndex], ref.current);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const size = optionsToDisplay.length;

    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === 'ArrowUp') {
      if (SKIP_KEYBOARD_NAV.includes(upItem)) {
        if (focusedItem > 0) {
          focusItem(focusedItem - 2);
        } else {
          focusItem(size - 2);
        }
      } else if (focusedItem > 0) {
        focusItem(focusedItem - 1);
      } else {
        focusItem(size - 1);
      }
    }
    if (e.key === 'ArrowDown') {
      if (SKIP_KEYBOARD_NAV.includes(downItem)) {
        if (focusedItem < size - 1) {
          focusItem(focusedItem + 2);
        } else {
          focusItem(1);
        }
      } else if (focusedItem < size - 1) {
        focusItem(focusedItem + 1);
      } else {
        focusItem(0);
      }
    }

    if (e.key === 'Enter') {
      // If options are not being shown, that means the TypeAhead already has a value
      // Therefore, pressing enter should not be prevented
      if (showOptions) {
        e.preventDefault();
      }
      const optionIsValid =
        allowCustom || options.map(item => item.label).includes(value) || optionsToDisplay[focusedItem];

      if (optionIsValid) {
        let option = {};
        if (focusedItem !== -1) {
          option = optionsToDisplay[focusedItem];
        } else {
          option = { label: value };
        }
        if (!keepOpen) {
          setShowOptions(false);
        }
        onChange(e, { value: optionLabel(option), option });
      } else {
        setShowOptions(false);
        setInvalid(true);
      }
    }

    if (e.key === 'Tab') {
      if (keepOpen) {
        setShowOptions(true);
      } else {
        setShowOptions(false);
      }
    }
  };

  const noMatchMessage = () => {
    if (emptyMessage) {
      return <>{emptyMessage}</>;
    }
    return <span>No matches found</span>;
  };

  const noMatch = () => {
    if (value && filteredOptions.length === 0 && !allowCustom && !loading) {
      return <DropdownItem role="option" disabled render={noMatchMessage} />;
    }
    return null;
  };

  const renderOption = item => {
    if (option) {
      return option({ onClick: e => handleClick(e, item), ...item });
    }
    return <DropdownButton onClick={e => handleClick(e, item)}>{optionLabel(item)}</DropdownButton>;
  };

  const shouldDisplayOptions = () => {
    if (allowCustom) {
      return showOptions && optionsToDisplay.length > 0;
    }
    return showOptions;
  };

  const renderNewTypeaheadItem = () => {
    if (loading) {
      return <LoadingState />;
    }

    return (
      <>
        {optionsToDisplay.map((item, key) => (
          <DropdownItem
            role="option"
            active={focusedItem === key}
            aria-selected={focusedItem === key}
            disabled={item.disabled || false}
            key={key}
            render={() => renderOption(item)}
          />
        ))}
      </>
    );
  };

  return (
    <ThemeProvider theme={{ dropdownIsTypeahead: true }}>
      <ThemeConsumer>
        {theme => (
          <DropdownWrapper ref={ref} {...otherProps} theme={{ width, ...theme }} data-testid={dataTestId}>
            {input({
              name,
              'aria-label': ariaLabel,
              value,
              onChange: handleChange,
              onFocus: handleFocus,
              onKeyDown: handleKeyDown,
              invalid,
              width,
            })}
            {shouldDisplayOptions() &&
              renderFloatingBox(
                doppelganger,
                (!isEmpty(optionsToDisplay) || !isNil(filterOptions)) && (
                  <DropdownBox
                    aria-label="Options list"
                    role="listbox"
                    theme={{
                      dropdownScrollable: scrollable,
                      inputWidth: width,
                      dropdownWidth,
                      dropdownMaxHeight,
                      ...theme,
                    }}
                    ref={self}
                    data-testid={dataTestId ? `${dataTestId}-box` : undefined}
                  >
                    {renderNewTypeaheadItem()}
                    {noMatch()}
                  </DropdownBox>
                ),
              )}
            {invalid && !allowCustom && <ErrorMessage as="div">Invalid option</ErrorMessage>}
          </DropdownWrapper>
        )}
      </ThemeConsumer>
    </ThemeProvider>
  );
}

CoreTypeahead.defaultProps = {
  keepOpen: false,
  option: undefined,
  allowCustom: false,
  emptyMessage: undefined,
  scrollable: false,
  width: 'large',
};

CoreTypeahead.displayName = 'CoreTypeahead';

export default CoreTypeahead;
